<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import OnboardingAPI from 'dashboard/api/onboarding';

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  hint: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue', 'validity-change']);

const { t } = useI18n();

const status = ref('idle'); // idle | checking | valid | invalid
const statusError = ref('');
const lastCheckedUrl = ref('');

const isValid = computed(() => status.value === 'valid');

watch(isValid, value => emit('validity-change', value), { immediate: true });

function looksLikeUrl(value) {
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    const withProtocol = trimmed.match(/^https?:\/\//i)
      ? trimmed
      : `https://${trimmed}`;
    const parsed = new URL(withProtocol);
    return Boolean(parsed.hostname && parsed.hostname.includes('.'));
  } catch {
    return false;
  }
}

const runCheck = useDebounceFn(async raw => {
  const value = (raw || '').trim();
  if (!value) {
    status.value = 'idle';
    statusError.value = '';
    lastCheckedUrl.value = '';
    return;
  }

  if (!looksLikeUrl(value)) {
    status.value = 'invalid';
    statusError.value = t('ONBOARDING_WIZARD.WELCOME.URL_INVALID');
    lastCheckedUrl.value = value;
    return;
  }

  status.value = 'checking';
  statusError.value = '';
  lastCheckedUrl.value = value;

  try {
    const { data } = await OnboardingAPI.validateUrl(value);
    if (lastCheckedUrl.value !== value) return;
    if (data?.valid) {
      status.value = 'valid';
      statusError.value = '';
      if (data.url && data.url !== props.modelValue) {
        emit('update:modelValue', data.url);
      }
    } else {
      status.value = 'invalid';
      statusError.value =
        data?.error || t('ONBOARDING_WIZARD.WELCOME.URL_UNREACHABLE');
    }
  } catch (error) {
    if (lastCheckedUrl.value !== value) return;
    status.value = 'invalid';
    statusError.value =
      error?.response?.data?.error ||
      t('ONBOARDING_WIZARD.WELCOME.URL_UNREACHABLE');
  }
}, 500);

watch(
  () => props.modelValue,
  value => {
    if (!value?.trim()) {
      status.value = 'idle';
      statusError.value = '';
      return;
    }
    runCheck(value);
  },
  { immediate: true }
);

function onInput(event) {
  emit('update:modelValue', event.target.value);
}

const tooltip = computed(() => {
  if (status.value === 'checking') {
    return t('ONBOARDING_WIZARD.WELCOME.URL_CHECKING');
  }
  if (status.value === 'valid') {
    return t('ONBOARDING_WIZARD.WELCOME.URL_OK');
  }
  if (status.value === 'invalid') {
    return statusError.value || t('ONBOARDING_WIZARD.WELCOME.URL_UNREACHABLE');
  }
  return '';
});
</script>

<template>
  <div class="w-full flex flex-col gap-2">
    <label
      v-if="label"
      class="text-sm font-semibold text-slate-700 dark:text-slate-200"
    >
      {{ label }}
    </label>

    <div
      class="flex items-center h-12 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden focus-within:ring-2 focus-within:ring-woot-500 focus-within:border-woot-500 transition-colors"
      :class="{
        'border-green-500 dark:border-green-500': status === 'valid',
        'border-red-400 dark:border-red-500': status === 'invalid',
      }"
    >
      <input
        type="url"
        :value="modelValue"
        :placeholder="placeholder"
        class="reset-base flex-1 h-full px-4 text-base bg-transparent text-slate-700 dark:text-slate-200 outline-none min-w-0 placeholder:text-slate-400 !mb-0"
        @input="onInput"
      />

      <div
        v-if="status !== 'idle'"
        v-tooltip.top="tooltip"
        class="flex-shrink-0 flex items-center justify-center w-11 h-full border-l border-slate-200 dark:border-slate-700"
      >
        <span
          v-if="status === 'checking'"
          class="size-4 border-2 border-woot-500 border-t-transparent rounded-full animate-spin"
        />
        <span
          v-else-if="status === 'valid'"
          class="i-lucide-check size-5 text-green-500"
        />
        <span v-else class="i-lucide-x size-5 text-red-500" />
      </div>
    </div>

    <p
      v-if="status === 'invalid' && statusError"
      class="text-sm text-red-600 dark:text-red-400 m-0"
    >
      {{ statusError }}
    </p>
    <p v-else-if="hint" class="text-sm text-slate-500 dark:text-slate-400 m-0">
      {{ hint }}
    </p>
  </div>
</template>
